/**
 * Gráfico de barras vertical en SVG puro. Sin dependencias.
 * Las labels van debajo del eje X; los valores se ven al hacer hover.
 */

interface DataPoint {
  label: string
  value: number
  /** Valor secundario opcional (p.ej. "pagado" vs "facturado"). */
  secondary?: number
}

interface Props {
  data: DataPoint[]
  /** Texto formateador para los valores (€, h, etc.). */
  formatValue?: (n: number) => string
  /** Alto del SVG en px. */
  height?: number
  /** Color principal del relleno (hex o tailwind-like). */
  primaryColor?: string
  secondaryColor?: string
  /** Si true, dibuja también la serie secundaria. */
  showSecondary?: boolean
}

export default function BarChart({
  data,
  formatValue = (n) => String(n),
  height = 220,
  primaryColor = '#a31b41',
  secondaryColor = '#10b981',
  showSecondary = false,
}: Props) {
  if (data.length === 0) {
    return (
      <div className="text-xs t-faint text-center py-8">Sin datos para mostrar.</div>
    )
  }

  const max = Math.max(1, ...data.map((d) => Math.max(d.value, d.secondary ?? 0)))
  const W = 600 // viewBox width
  const H = height
  const padding = { top: 16, right: 16, bottom: 32, left: 48 }
  const innerW = W - padding.left - padding.right
  const innerH = H - padding.top - padding.bottom
  const barGroupW = innerW / data.length
  const barW = showSecondary ? barGroupW * 0.35 : barGroupW * 0.55
  const gap = showSecondary ? barGroupW * 0.05 : 0

  // 4 líneas horizontales de referencia (gridlines).
  const grid = [0.25, 0.5, 0.75, 1].map((p) => {
    const y = padding.top + innerH * (1 - p)
    return { y, label: formatValue(max * p) }
  })

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Grid */}
      {grid.map((g, i) => (
        <g key={i}>
          <line
            x1={padding.left}
            x2={W - padding.right}
            y1={g.y}
            y2={g.y}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray="2 3"
          />
          <text
            x={padding.left - 6}
            y={g.y + 4}
            textAnchor="end"
            className="text-[9px] fill-current opacity-50"
          >
            {g.label}
          </text>
        </g>
      ))}

      {/* Barras */}
      {data.map((d, i) => {
        const groupX = padding.left + i * barGroupW
        const xPrimary = showSecondary
          ? groupX + barGroupW / 2 - barW - gap / 2
          : groupX + (barGroupW - barW) / 2
        const xSecondary = groupX + barGroupW / 2 + gap / 2
        const hPrimary = (d.value / max) * innerH
        const hSecondary = ((d.secondary ?? 0) / max) * innerH
        return (
          <g key={i}>
            <rect
              x={xPrimary}
              y={padding.top + innerH - hPrimary}
              width={barW}
              height={hPrimary}
              fill={primaryColor}
              rx={2}
            >
              <title>
                {d.label}: {formatValue(d.value)}
                {showSecondary && d.secondary != null
                  ? `\nSecundario: ${formatValue(d.secondary)}`
                  : ''}
              </title>
            </rect>
            {showSecondary && d.secondary != null && (
              <rect
                x={xSecondary}
                y={padding.top + innerH - hSecondary}
                width={barW}
                height={hSecondary}
                fill={secondaryColor}
                fillOpacity={0.85}
                rx={2}
              >
                <title>{formatValue(d.secondary)}</title>
              </rect>
            )}
            <text
              x={groupX + barGroupW / 2}
              y={H - padding.bottom + 14}
              textAnchor="middle"
              className="text-[9px] fill-current opacity-60"
            >
              {d.label}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
